// src/modules/Admin/pages/Blog/BlogEditor.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaSave,
  FaEye,
  FaArrowLeft,
  FaImage,
  FaTimes,
  FaMagic,
  FaRobot,
  FaLightbulb,
  FaTags,
  FaSpinner,
  FaPen,
  FaBullhorn,
  FaCheck,
  FaUserTie,
  FaNewspaper,
  FaGraduationCap,
  FaSearch,
  FaEdit,
  FaLink,
  FaLinkedin,
  FaCopy,
  FaFilePdf,
  FaUpload,
  FaTrash,
  FaFileAlt
} from 'react-icons/fa';
import * as aiService from '../../../../services/aiService';
import * as linkedinService from '../../../../services/linkedinService';

// Styled Components
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #111;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;
  border: none;

  &.primary {
    background: #111;
    color: white;

    &:hover {
      background: #333;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: white;
    color: #666;
    border: 1px solid #e5e5e5;

    &:hover {
      background: #f5f5f5;
      color: #111;
    }
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #111;
`;

const CardContent = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #333;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: ${props => props.$minHeight || '100px'};
  font-family: inherit;
  transition: all 200ms;

  @media (max-width: 640px) {
    min-height: ${props => props.$minHeight === '300px' ? '200px' : props.$minHeight || '80px'};
    font-size: 16px; /* Previne zoom no iOS */
  }

  &:focus {
    outline: none;
    border-color: #333;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const ImageUploadArea = styled.div`
  position: relative;
  border: 2px dashed #e5e5e5;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  transition: all 200ms;
  cursor: pointer;
  background: ${props => props.$hasImage ? `url(${props.$image}) center/cover` : '#fafafa'};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 640px) {
    padding: 24px 16px;
    min-height: 150px;
  }

  &:hover {
    border-color: #ccc;
    background-color: ${props => props.$hasImage ? 'rgba(0,0,0,0.5)' : '#f5f5f5'};
  }

  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 200ms;
  border-radius: 6px;

  ${ImageUploadArea}:hover & {
    opacity: 1;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;

  &:hover {
    background: #f5f5f5;
  }
`;

const UploadIcon = styled.div`
  font-size: 32px;
  color: ${props => props.$hasImage ? 'white' : '#ccc'};
  margin-bottom: 12px;
`;

const UploadText = styled.p`
  font-size: 14px;
  color: ${props => props.$hasImage ? 'white' : '#666'};
`;

const HelpText = styled.p`
  font-size: 12px;
  color: #999;
  margin-top: 8px;
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AIButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  font-size: 12px;
  color: white;
  cursor: pointer;
  transition: all 200ms;
  margin-top: 8px;
  margin-right: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    animation: ${props => props.$loading ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const AIButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const AIGenerateCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);

  @media (max-width: 640px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const AIGenerateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    font-size: 22px;
  }
`;

const AIGenerateSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
`;

const AIGenerateInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  font-size: 15px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;

  &::placeholder {
    color: #888;
  }

  &:focus {
    outline: none;
    border-color: white;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.2);
  }
`;

const AIGenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #667eea;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 18px;
    animation: ${props => props.$loading ? 'spin 1s linear infinite' : 'none'};
  }
`;

const AIGenerateHint = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 12px;
`;

const AgentProgress = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
`;

const AgentStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const AgentIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => {
    if (props.$status === 'completed') return '#10b981';
    if (props.$status === 'active') return 'white';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  color: ${props => {
    if (props.$status === 'active') return '#667eea';
    return 'white';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 300ms;

  svg {
    animation: ${props => props.$status === 'active' ? 'spin 1s linear infinite' : 'none'};
  }
`;

const AgentInfo = styled.div`
  flex: 1;
`;

const AgentName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$status === 'pending' ? 'rgba(255, 255, 255, 0.5)' : 'white'};
`;

const AgentDescription = styled.div`
  font-size: 11px;
  color: ${props => props.$status === 'pending' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.7)'};
`;

const TeamSelector = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const TeamOption = styled.button`
  flex: 1;
  padding: 16px;
  border: 2px solid ${props => props.$selected ? 'white' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 12px;
  background: ${props => props.$selected ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  cursor: pointer;
  transition: all 200ms;
  text-align: left;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TeamIcon = styled.div`
  font-size: 24px;
  color: white;
  margin-bottom: 8px;
`;

const TeamName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
`;

const TeamDescription = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const TeamBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${props => props.$type === 'educativa' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
  border-radius: 4px;
  font-size: 10px;
  color: white;
  margin-top: 8px;
`;

const UrlInputWrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const UrlInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.1);
  color: white;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const UrlIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
`;

const UrlHint = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 6px;
  margin-left: 4px;
`;

// LinkedIn Styles
const LinkedInCard = styled.div`
  background: linear-gradient(135deg, #0077B5 0%, #005582 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 119, 181, 0.2);
`;

const LinkedInTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    font-size: 20px;
  }
`;

const LinkedInSubtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 16px;
`;

const LinkedInPreview = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
`;

const LinkedInActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const LinkedInButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${props => props.$variant === 'secondary' ? 'rgba(255, 255, 255, 0.15)' : 'white'};
  color: ${props => props.$variant === 'secondary' ? 'white' : '#0077B5'};
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    animation: ${props => props.$loading ? 'spin 1s linear infinite' : 'none'};
  }
`;

const LinkedInStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);

  &.connected {
    background: rgba(16, 185, 129, 0.2);
  }
`;

const SuccessMessage = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  background: #10b981;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  z-index: 1000;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    status: 'draft',
    category: '',
    tags: '',
    meta_title: '',
    meta_description: '',
    published_at: new Date().toISOString().split('T')[0] // Data de publicação (YYYY-MM-DD)
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Estados para IA
  const [aiTopic, setAiTopic] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState('educativa');
  const [generatingPost, setGeneratingPost] = useState(false);
  const [generatingExcerpt, setGeneratingExcerpt] = useState(false);
  const [generatingTags, setGeneratingTags] = useState(false);
  const [generatingMeta, setGeneratingMeta] = useState(false);
  // Agentes da Equipe Educativa
  const [agentProgress, setAgentProgress] = useState({
    redator: 'pending',
    marketeiro: 'pending',
    revisor: 'pending'
  });
  // Agentes da Equipe Jornalística
  const [agentProgressJornalistico, setAgentProgressJornalistico] = useState({
    pesquisador: 'pending',
    reporter: 'pending',
    editor: 'pending'
  });

  // Estados para LinkedIn
  const [linkedInPost, setLinkedInPost] = useState('');
  const [generatingLinkedIn, setGeneratingLinkedIn] = useState(false);
  const [publishingLinkedIn, setPublishingLinkedIn] = useState(false);
  const [linkedInConnected, setLinkedInConnected] = useState(false);
  const [linkedInImage, setLinkedInImage] = useState(null);
  const [linkedInImagePreview, setLinkedInImagePreview] = useState('');
  const [useLinkedInCoverImage, setUseLinkedInCoverImage] = useState(false);

  // Estados para PDF
  const [pdfContent, setPdfContent] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('');
  const [processingPdf, setProcessingPdf] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ stage: '', message: '' });
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Estados para tradução
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState('');

  // Verifica conexão com LinkedIn ao montar e escuta mensagens do popup
  useEffect(() => {
    setLinkedInConnected(linkedinService.isLinkedInConnected());

    // Listener para receber mensagem de sucesso do popup OAuth
    const handleMessage = (event) => {
      if (event.data?.type === 'linkedin_auth_success') {
        setLinkedInConnected(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          title: data.title || '',
          slug: data.slug || '',
          excerpt: data.excerpt || '',
          content: data.content || '',
          cover_image: data.cover_image || '',
          status: data.status || 'draft',
          category: data.category || '',
          tags: data.tags?.join(', ') || '',
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          published_at: data.published_at ? data.published_at.split('T')[0] : (data.created_at ? data.created_at.split('T')[0] : new Date().toISOString().split('T')[0])
        });
      }
    } catch (error) {
      console.error('Erro ao carregar post:', error);
      alert('Erro ao carregar post.');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !isEditing) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `blog/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, cover_image: data.publicUrl }));
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload da imagem. Tente novamente.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, cover_image: '' }));
  };

  const handleSubmit = async (status = formData.status, generateTranslations = true) => {
    if (!formData.title.trim()) {
      alert('O título é obrigatório.');
      return;
    }

    setSaving(true);
    try {
      const postData = {
        title: formData.title,
        slug: formData.slug || formData.title.toLowerCase().replace(/\s+/g, '-'),
        excerpt: formData.excerpt,
        content: formData.content,
        cover_image: formData.cover_image,
        status: status,
        category: formData.category,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        meta_title: formData.meta_title || formData.title,
        meta_description: formData.meta_description || formData.excerpt,
        author_id: user?.id,
        author_name: user?.user_metadata?.full_name || user?.email,
        author_avatar: user?.user_metadata?.avatar_url,
        updated_at: new Date().toISOString(),
        published_at: formData.published_at ? new Date(formData.published_at).toISOString() : new Date().toISOString()
      };

      // Gerar traduções se habilitado e tiver conteúdo
      if (generateTranslations && formData.content && formData.title) {
        setTranslating(true);
        try {
          const translations = await aiService.translateBlogContent(
            {
              title: formData.title,
              excerpt: formData.excerpt,
              content: formData.content,
              meta_description: formData.meta_description
            },
            (stage, message) => setTranslationProgress(message)
          );

          // Adicionar traduções ao postData
          postData.title_en = translations.en.title_en;
          postData.excerpt_en = translations.en.excerpt_en;
          postData.content_en = translations.en.content_en;
          postData.meta_description_en = translations.en.meta_description_en;

          postData.title_es = translations.es.title_es;
          postData.excerpt_es = translations.es.excerpt_es;
          postData.content_es = translations.es.content_es;
          postData.meta_description_es = translations.es.meta_description_es;
        } catch (translationError) {
          console.error('Erro ao traduzir:', translationError);
          // Continua salvando sem traduções se falhar
        } finally {
          setTranslating(false);
          setTranslationProgress('');
        }
      }

      if (isEditing) {
        const { error } = await supabase
          .from('posts')
          .update(postData)
          .eq('id', id);

        if (error) throw error;
      } else {
        postData.created_at = formData.published_at ? new Date(formData.published_at).toISOString() : new Date().toISOString();
        postData.views = 0;

        const { error } = await supabase
          .from('posts')
          .insert([postData]);

        if (error) throw error;
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate('/admin/blog');
      }, 2000);
    } catch (error) {
      console.error('Erro ao salvar post:', error);
      alert('Erro ao salvar post. Verifique se a tabela "posts" existe no Supabase.');
    } finally {
      setSaving(false);
    }
  };

  // Funções de IA
  const handleGeneratePost = async () => {
    if (!aiTopic.trim()) {
      alert('Digite um tema para gerar o post.');
      return;
    }

    setGeneratingPost(true);

    // Resetar progresso de ambas as equipes
    setAgentProgress({
      redator: 'pending',
      marketeiro: 'pending',
      revisor: 'pending'
    });
    setAgentProgressJornalistico({
      pesquisador: 'pending',
      reporter: 'pending',
      editor: 'pending'
    });

    try {
      // FASE 0: Buscar conteúdo da URL de referência (se houver)
      let referenceContent = null;
      if (referenceUrl.trim()) {
        setFetchingUrl(true);
        try {
          referenceContent = await aiService.fetchUrlContent(referenceUrl.trim());
        } catch (urlError) {
          console.error('Erro ao buscar URL:', urlError);
          alert(`Erro ao ler a URL de referência: ${urlError.message}\n\nA geração continuará sem a referência.`);
        } finally {
          setFetchingUrl(false);
        }
      }

      // Callback para atualizar o progresso dos agentes - EQUIPE EDUCATIVA
      const onProgressEducativa = (agent, message) => {
        if (agent === 'redator') {
          setAgentProgress({ redator: 'active', marketeiro: 'pending', revisor: 'pending' });
        } else if (agent === 'marketeiro') {
          setAgentProgress({ redator: 'completed', marketeiro: 'active', revisor: 'pending' });
        } else if (agent === 'revisor') {
          setAgentProgress({ redator: 'completed', marketeiro: 'completed', revisor: 'active' });
        } else if (agent === 'concluido') {
          setAgentProgress({ redator: 'completed', marketeiro: 'completed', revisor: 'completed' });
        }
      };

      // Callback para atualizar o progresso dos agentes - EQUIPE JORNALÍSTICA
      const onProgressJornalistico = (agent, message) => {
        if (agent === 'pesquisador') {
          setAgentProgressJornalistico({ pesquisador: 'active', reporter: 'pending', editor: 'pending' });
        } else if (agent === 'reporter') {
          setAgentProgressJornalistico({ pesquisador: 'completed', reporter: 'active', editor: 'pending' });
        } else if (agent === 'editor') {
          setAgentProgressJornalistico({ pesquisador: 'completed', reporter: 'completed', editor: 'active' });
        } else if (agent === 'concluido') {
          setAgentProgressJornalistico({ pesquisador: 'completed', reporter: 'completed', editor: 'completed' });
        }
      };

      // 1. Gerar o conteúdo usando a equipe selecionada
      const onProgress = selectedTeam === 'jornalistica' ? onProgressJornalistico : onProgressEducativa;
      const content = await aiService.generatePostWithTeam(aiTopic, '', selectedTeam, onProgress, referenceContent);

      // 2. Gerar títulos sugeridos pela IA e usar o primeiro
      const titles = await aiService.generateTitles(content);
      const title = titles[0] || aiTopic;

      // 3. Gerar slug do título
      const slug = title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      // Atualizar formulário com conteúdo e título
      setFormData(prev => ({ ...prev, content, title, slug }));

      // 4. Gerar resumo, tags e meta descrição em paralelo
      const [excerpt, tags, metaDescription] = await Promise.all([
        aiService.generateExcerpt(content),
        aiService.generateTags(content),
        aiService.generateMetaDescription(title, content)
      ]);

      // 5. Atualizar todos os campos
      setFormData(prev => ({
        ...prev,
        excerpt,
        tags,
        meta_description: metaDescription,
        meta_title: title
      }));

    } catch (error) {
      console.error('Erro ao gerar post:', error);
      alert('Erro ao gerar post com IA. Tente novamente.');
      setAgentProgress({
        redator: 'pending',
        marketeiro: 'pending',
        revisor: 'pending'
      });
      setAgentProgressJornalistico({
        pesquisador: 'pending',
        reporter: 'pending',
        editor: 'pending'
      });
    } finally {
      setGeneratingPost(false);
    }
  };

  const handleGenerateExcerpt = async () => {
    if (!formData.content) {
      alert('Adicione conteúdo antes de gerar o resumo.');
      return;
    }

    setGeneratingExcerpt(true);
    try {
      const excerpt = await aiService.generateExcerpt(formData.content);
      setFormData(prev => ({ ...prev, excerpt }));
    } catch (error) {
      console.error('Erro ao gerar resumo:', error);
      alert('Erro ao gerar resumo com IA. Tente novamente.');
    } finally {
      setGeneratingExcerpt(false);
    }
  };

  const handleGenerateTags = async () => {
    if (!formData.content) {
      alert('Adicione conteúdo antes de gerar as tags.');
      return;
    }

    setGeneratingTags(true);
    try {
      const tags = await aiService.generateTags(formData.content);
      setFormData(prev => ({ ...prev, tags }));
    } catch (error) {
      console.error('Erro ao gerar tags:', error);
      alert('Erro ao gerar tags com IA. Tente novamente.');
    } finally {
      setGeneratingTags(false);
    }
  };

  const handleGenerateMeta = async () => {
    if (!formData.content || !formData.title) {
      alert('Adicione título e conteúdo antes de gerar a meta descrição.');
      return;
    }

    setGeneratingMeta(true);
    try {
      const metaDescription = await aiService.generateMetaDescription(formData.title, formData.content);
      setFormData(prev => ({ ...prev, meta_description: metaDescription }));
    } catch (error) {
      console.error('Erro ao gerar meta descrição:', error);
      alert('Erro ao gerar meta descrição com IA. Tente novamente.');
    } finally {
      setGeneratingMeta(false);
    }
  };

  // Funções do LinkedIn
  const handleGenerateLinkedInPost = async () => {
    if (!formData.content || !formData.title) {
      alert('Adicione título e conteúdo antes de gerar o post do LinkedIn.');
      return;
    }

    setGeneratingLinkedIn(true);
    try {
      // Gera a URL do blog post (assumindo estrutura /blog/slug)
      const blogUrl = formData.slug ? `https://blog.vegarobotics.com.br/${formData.slug}` : '';
      const post = await aiService.generateLinkedInPost(formData.title, formData.content, blogUrl);
      setLinkedInPost(post);
    } catch (error) {
      console.error('Erro ao gerar post do LinkedIn:', error);
      alert('Erro ao gerar post do LinkedIn. Tente novamente.');
    } finally {
      setGeneratingLinkedIn(false);
    }
  };

  const handleConnectLinkedIn = () => {
    if (!linkedinService.isLinkedInConfigured()) {
      alert('LinkedIn não configurado. Adicione REACT_APP_LINKEDIN_CLIENT_ID no .env');
      return;
    }
    linkedinService.openAuthPopup();
  };

  const handlePublishLinkedIn = async () => {
    if (!linkedInPost) {
      alert('Gere o post do LinkedIn primeiro.');
      return;
    }

    if (!linkedinService.isLinkedInConnected()) {
      alert('Conecte sua conta do LinkedIn primeiro.');
      return;
    }

    setPublishingLinkedIn(true);
    try {
      let result;

      if (linkedInImage) {
        // Publicar com imagem
        result = await linkedinService.publishPostWithImage(linkedInPost, linkedInImage);
      } else {
        // Publicar sem imagem (com link do artigo se disponível)
        const blogUrl = formData.slug ? `https://blog.vegarobotics.com.br/${formData.slug}` : null;
        result = await linkedinService.publishPost(linkedInPost, blogUrl);
      }

      alert(result.message);

      // Limpar imagem após publicar
      handleRemoveLinkedInImage();
    } catch (error) {
      console.error('Erro ao publicar no LinkedIn:', error);
      alert(`Erro ao publicar: ${error.message}`);
    } finally {
      setPublishingLinkedIn(false);
    }
  };

  const handleCopyLinkedInPost = () => {
    if (!linkedInPost) return;
    navigator.clipboard.writeText(linkedInPost);
    alert('Post copiado para a área de transferência!');
  };

  // Handler para upload de imagem do LinkedIn
  const handleLinkedInImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida.');
      return;
    }

    // Validar tamanho (máximo 8MB para LinkedIn)
    if (file.size > 8 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 8MB.');
      return;
    }

    setLinkedInImage(file);
    setUseLinkedInCoverImage(false);

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLinkedInImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handler para usar imagem de capa do blog
  const handleUseLinkedInCoverImage = async () => {
    if (!formData.cover_image) {
      alert('Adicione uma imagem de capa ao post primeiro.');
      return;
    }

    try {
      // Fetch a imagem de capa e converter para blob
      const response = await fetch(formData.cover_image);
      const blob = await response.blob();
      const file = new File([blob], 'cover-image.jpg', { type: blob.type });

      setLinkedInImage(file);
      setLinkedInImagePreview(formData.cover_image);
      setUseLinkedInCoverImage(true);
    } catch (error) {
      console.error('Erro ao carregar imagem de capa:', error);
      alert('Erro ao carregar imagem de capa. Tente fazer upload manual.');
    }
  };

  // Remover imagem do LinkedIn
  const handleRemoveLinkedInImage = () => {
    setLinkedInImage(null);
    setLinkedInImagePreview('');
    setUseLinkedInCoverImage(false);
  };

  // ==========================================
  // HANDLERS DE PDF
  // ==========================================

  // Upload e extração de PDF local
  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Por favor, selecione um arquivo PDF.');
      return;
    }

    setProcessingPdf(true);
    setPdfProgress({ stage: 'extracting', message: 'Extraindo texto do PDF...' });

    try {
      const result = await aiService.extractPdfText(file);
      setPdfContent(result);
      setPdfFilename(file.name);
      setShowPdfPreview(true);

      // Limpar input para permitir mesmo arquivo novamente
      event.target.value = '';
    } catch (error) {
      console.error('Erro ao processar PDF:', error);
      alert('Erro ao processar PDF: ' + error.message);
    } finally {
      setProcessingPdf(false);
      setPdfProgress({ stage: '', message: '' });
    }
  };

  // Gerar artigo a partir do PDF
  const handleGenerateFromPdf = async () => {
    if (!pdfContent?.content) {
      alert('Nenhum PDF carregado. Faça upload de um PDF primeiro.');
      return;
    }

    setGeneratingPost(true);
    setPdfProgress({ stage: 'analyzing', message: 'Analisando conteúdo do PDF...' });

    // Resetar progresso de ambas as equipes
    setAgentProgress({
      redator: 'pending',
      marketeiro: 'pending',
      revisor: 'pending'
    });
    setAgentProgressJornalistico({
      pesquisador: 'pending',
      reporter: 'pending',
      editor: 'pending'
    });

    try {
      const result = await aiService.generatePostFromPdf(
        pdfContent.content,
        aiTopic.trim() || '', // usar tema se fornecido, senão extrair do PDF
        selectedTeam,
        (stage, message) => {
          setPdfProgress({ stage, message });

          // Atualizar progresso dos agentes baseado no estágio
          if (selectedTeam === 'educativa') {
            if (stage === 'redator') {
              setAgentProgress({ redator: 'active', marketeiro: 'pending', revisor: 'pending' });
            } else if (stage === 'marketeiro') {
              setAgentProgress({ redator: 'completed', marketeiro: 'active', revisor: 'pending' });
            } else if (stage === 'revisor') {
              setAgentProgress({ redator: 'completed', marketeiro: 'completed', revisor: 'active' });
            } else if (stage === 'completed') {
              setAgentProgress({ redator: 'completed', marketeiro: 'completed', revisor: 'completed' });
            }
          } else {
            if (stage === 'pesquisador') {
              setAgentProgressJornalistico({ pesquisador: 'active', reporter: 'pending', editor: 'pending' });
            } else if (stage === 'reporter') {
              setAgentProgressJornalistico({ pesquisador: 'completed', reporter: 'active', editor: 'pending' });
            } else if (stage === 'editor') {
              setAgentProgressJornalistico({ pesquisador: 'completed', reporter: 'completed', editor: 'active' });
            } else if (stage === 'completed') {
              setAgentProgressJornalistico({ pesquisador: 'completed', reporter: 'completed', editor: 'completed' });
            }
          }
        }
      );

      // Atualizar formulário com TODOS os campos gerados
      setFormData(prev => ({
        ...prev,
        title: result.title || prev.title,
        slug: result.title ? result.title.toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') : prev.slug,
        content: result.article,
        excerpt: result.excerpt || prev.excerpt,
        meta_description: result.meta_description || prev.meta_description,
        tags: Array.isArray(result.tags) ? result.tags.join(', ') : prev.tags,
        category: result.category || prev.category
      }));

      // Limpar PDF após gerar
      setShowPdfPreview(false);

    } catch (error) {
      console.error('Erro ao gerar artigo do PDF:', error);
      alert('Erro ao gerar artigo: ' + error.message);
    } finally {
      setGeneratingPost(false);
      setPdfProgress({ stage: '', message: '' });
    }
  };

  // Limpar PDF carregado
  const handleClearPdf = () => {
    setPdfContent(null);
    setPdfFilename('');
    setShowPdfPreview(false);
  };

  if (loading) {
    return (
      <Container>
        <p>Carregando...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/admin/blog')}>
          <FaArrowLeft />
          Voltar
        </BackButton>
        <HeaderActions>
          <Button
            className="secondary"
            onClick={() => handleSubmit('draft', false)}
            disabled={saving || translating}
          >
            Salvar Rascunho
          </Button>
          <Button
            className="primary"
            onClick={() => handleSubmit('published')}
            disabled={saving || translating}
          >
            {translating ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSave />}
            {translating ? translationProgress || 'Traduzindo...' : saving ? 'Salvando...' : 'Publicar'}
          </Button>
        </HeaderActions>
      </Header>

      {/* Card de Geração com IA */}
      <AIGenerateCard>
        <AIGenerateTitle>
          <FaRobot />
          Gerar Post com Sistema Multiagente
        </AIGenerateTitle>
        <AIGenerateSubtitle>
          Escolha uma equipe de IA e deixe 3 agentes especializados criarem seu conteúdo.
        </AIGenerateSubtitle>

        {/* Seletor de Equipes */}
        <TeamSelector>
          <TeamOption
            $selected={selectedTeam === 'educativa'}
            onClick={() => setSelectedTeam('educativa')}
            disabled={generatingPost}
          >
            <TeamIcon><FaGraduationCap /></TeamIcon>
            <TeamName>Equipe Educativa</TeamName>
            <TeamDescription>
              Redator Técnico → Marketing B2B → Revisor Editorial
            </TeamDescription>
            <TeamBadge $type="educativa">Tutoriais e guias práticos</TeamBadge>
          </TeamOption>

          <TeamOption
            $selected={selectedTeam === 'jornalistica'}
            onClick={() => setSelectedTeam('jornalistica')}
            disabled={generatingPost}
          >
            <TeamIcon><FaNewspaper /></TeamIcon>
            <TeamName>Equipe Jornalística</TeamName>
            <TeamDescription>
              Pesquisador → Repórter Setorial → Editor-Chefe
            </TeamDescription>
            <TeamBadge $type="jornalistica">Notícias e análises de mercado</TeamBadge>
          </TeamOption>
        </TeamSelector>

        {/* Campo de URL de Referência */}
        <UrlInputWrapper>
          <UrlIcon><FaLink /></UrlIcon>
          <UrlInput
            type="url"
            placeholder="URL de referência (opcional) - Ex: https://noticias.portaldaindustria.com.br/..."
            value={referenceUrl}
            onChange={(e) => setReferenceUrl(e.target.value)}
            disabled={generatingPost || fetchingUrl || pdfContent}
          />
          <UrlHint>
            Cole uma URL para usar como referência de estilo, dados e estrutura (não será copiado)
          </UrlHint>
        </UrlInputWrapper>

        {/* Divisor OU */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          margin: '16px 0',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.2)' }} />
          <span style={{ fontSize: '12px', fontWeight: '500' }}>OU IMPORTAR DE PDF</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.2)' }} />
        </div>

        {/* Upload de PDF */}
        <div style={{ marginBottom: '16px' }}>
          {!pdfContent ? (
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '20px',
              border: '2px dashed rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              cursor: processingPdf ? 'not-allowed' : 'pointer',
              transition: 'all 200ms',
              background: 'rgba(255, 255, 255, 0.05)',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px'
            }}>
              {processingPdf ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  {pdfProgress.message || 'Processando PDF...'}
                </>
              ) : (
                <>
                  <FaFilePdf style={{ fontSize: '20px' }} />
                  <span>Clique para fazer upload de um PDF</span>
                </>
              )}
              <input
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                disabled={processingPdf || generatingPost}
                style={{ display: 'none' }}
              />
            </label>
          ) : (
            <div style={{
              padding: '16px',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '10px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white' }}>
                  <FaFilePdf style={{ color: '#10b981', fontSize: '20px' }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{pdfFilename}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {pdfContent.totalChars?.toLocaleString()} caracteres
                      {pdfContent.totalPages && ` · ${pdfContent.totalPages} páginas`}
                      {pdfContent.truncated && ' (truncado)'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleClearPdf}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fca5a5',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  <FaTrash /> Remover
                </button>
              </div>

              {showPdfPreview && (
                <div style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>
                  {pdfContent.content?.substring(0, 1500)}
                  {pdfContent.content?.length > 1500 && '...'}
                </div>
              )}

              <button
                onClick={() => setShowPdfPreview(!showPdfPreview)}
                style={{
                  marginTop: '8px',
                  padding: '4px 8px',
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                {showPdfPreview ? 'Ocultar preview' : 'Ver preview do conteúdo'}
              </button>
            </div>
          )}
        </div>

        <AIGenerateInput
          type="text"
          placeholder={pdfContent
            ? "Tema/foco do artigo (opcional - será extraído do PDF se vazio)"
            : selectedTeam === 'jornalistica'
              ? "Ex: Investimentos em automação industrial no Brasil em 2024"
              : "Ex: Como a visão computacional está revolucionando o controle de qualidade"
          }
          value={aiTopic}
          onChange={(e) => setAiTopic(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !generatingPost) {
              if (pdfContent) {
                handleGenerateFromPdf();
              } else if (aiTopic.trim()) {
                handleGeneratePost();
              }
            }
          }}
          disabled={generatingPost}
        />

        {/* Botões de geração */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {pdfContent ? (
            /* Botão para gerar do PDF */
            <AIGenerateButton
              onClick={handleGenerateFromPdf}
              disabled={generatingPost}
              $loading={generatingPost}
              style={{ background: '#10b981' }}
            >
              {generatingPost ? <FaSpinner /> : <FaFilePdf />}
              {generatingPost ? 'Gerando do PDF...' : `Gerar do PDF com ${selectedTeam === 'jornalistica' ? 'Equipe Jornalística' : 'Equipe Educativa'}`}
            </AIGenerateButton>
          ) : (
            /* Botão normal para gerar de tema */
            <AIGenerateButton
              onClick={handleGeneratePost}
              disabled={generatingPost || fetchingUrl || !aiTopic.trim()}
              $loading={generatingPost || fetchingUrl}
            >
              {fetchingUrl ? <FaSpinner /> : generatingPost ? <FaSpinner /> : <FaMagic />}
              {fetchingUrl ? 'Lendo URL de referência...' : generatingPost ? 'Agentes trabalhando...' : `Gerar com ${selectedTeam === 'jornalistica' ? 'Equipe Jornalística' : 'Equipe Educativa'}`}
            </AIGenerateButton>
          )}
        </div>

        {/* Indicador de progresso do PDF */}
        {generatingPost && pdfContent && pdfProgress.message && (
          <div style={{
            marginTop: '12px',
            padding: '10px 16px',
            background: 'rgba(16, 185, 129, 0.15)',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
            {pdfProgress.message}
          </div>
        )}

        {/* Progresso da Equipe Educativa */}
        {generatingPost && selectedTeam === 'educativa' && (
          <AgentProgress>
            <AgentStep>
              <AgentIcon $status={agentProgress.redator}>
                {agentProgress.redator === 'completed' ? <FaCheck /> :
                 agentProgress.redator === 'active' ? <FaSpinner /> : <FaPen />}
              </AgentIcon>
              <AgentInfo>
                <AgentName $status={agentProgress.redator}>Redator Técnico Industrial</AgentName>
                <AgentDescription $status={agentProgress.redator}>
                  {agentProgress.redator === 'active' ? 'Criando conteúdo educativo...' :
                   agentProgress.redator === 'completed' ? 'Conteúdo base criado' :
                   'Especialista em tecnologia e indústria'}
                </AgentDescription>
              </AgentInfo>
            </AgentStep>

            <AgentStep>
              <AgentIcon $status={agentProgress.marketeiro}>
                {agentProgress.marketeiro === 'completed' ? <FaCheck /> :
                 agentProgress.marketeiro === 'active' ? <FaSpinner /> : <FaBullhorn />}
              </AgentIcon>
              <AgentInfo>
                <AgentName $status={agentProgress.marketeiro}>Especialista em Marketing B2B</AgentName>
                <AgentDescription $status={agentProgress.marketeiro}>
                  {agentProgress.marketeiro === 'active' ? 'Integrando soluções da Vega...' :
                   agentProgress.marketeiro === 'completed' ? 'CTAs e soluções integradas' :
                   'Integração natural de produtos e CTAs'}
                </AgentDescription>
              </AgentInfo>
            </AgentStep>

            <AgentStep>
              <AgentIcon $status={agentProgress.revisor}>
                {agentProgress.revisor === 'completed' ? <FaCheck /> :
                 agentProgress.revisor === 'active' ? <FaSpinner /> : <FaUserTie />}
              </AgentIcon>
              <AgentInfo>
                <AgentName $status={agentProgress.revisor}>Revisor Editorial Sênior</AgentName>
                <AgentDescription $status={agentProgress.revisor}>
                  {agentProgress.revisor === 'active' ? 'Revisando e polindo o texto...' :
                   agentProgress.revisor === 'completed' ? 'Texto finalizado e revisado' :
                   'Garante qualidade e fluidez'}
                </AgentDescription>
              </AgentInfo>
            </AgentStep>
          </AgentProgress>
        )}

        {/* Progresso da Equipe Jornalística */}
        {generatingPost && selectedTeam === 'jornalistica' && (
          <AgentProgress>
            <AgentStep>
              <AgentIcon $status={agentProgressJornalistico.pesquisador}>
                {agentProgressJornalistico.pesquisador === 'completed' ? <FaCheck /> :
                 agentProgressJornalistico.pesquisador === 'active' ? <FaSpinner /> : <FaSearch />}
              </AgentIcon>
              <AgentInfo>
                <AgentName $status={agentProgressJornalistico.pesquisador}>Pesquisador de Dados</AgentName>
                <AgentDescription $status={agentProgressJornalistico.pesquisador}>
                  {agentProgressJornalistico.pesquisador === 'active' ? 'Levantando dados e fontes...' :
                   agentProgressJornalistico.pesquisador === 'completed' ? 'Dados e fontes compilados' :
                   'Especialista em jornalismo de dados'}
                </AgentDescription>
              </AgentInfo>
            </AgentStep>

            <AgentStep>
              <AgentIcon $status={agentProgressJornalistico.reporter}>
                {agentProgressJornalistico.reporter === 'completed' ? <FaCheck /> :
                 agentProgressJornalistico.reporter === 'active' ? <FaSpinner /> : <FaNewspaper />}
              </AgentIcon>
              <AgentInfo>
                <AgentName $status={agentProgressJornalistico.reporter}>Repórter Setorial</AgentName>
                <AgentDescription $status={agentProgressJornalistico.reporter}>
                  {agentProgressJornalistico.reporter === 'active' ? 'Escrevendo artigo jornalístico...' :
                   agentProgressJornalistico.reporter === 'completed' ? 'Artigo base escrito' :
                   'Cobre indústria e economia'}
                </AgentDescription>
              </AgentInfo>
            </AgentStep>

            <AgentStep>
              <AgentIcon $status={agentProgressJornalistico.editor}>
                {agentProgressJornalistico.editor === 'completed' ? <FaCheck /> :
                 agentProgressJornalistico.editor === 'active' ? <FaSpinner /> : <FaEdit />}
              </AgentIcon>
              <AgentInfo>
                <AgentName $status={agentProgressJornalistico.editor}>Editor-Chefe</AgentName>
                <AgentDescription $status={agentProgressJornalistico.editor}>
                  {agentProgressJornalistico.editor === 'active' ? 'Revisando e finalizando...' :
                   agentProgressJornalistico.editor === 'completed' ? 'Artigo finalizado' :
                   'Garante qualidade editorial'}
                </AgentDescription>
              </AgentInfo>
            </AgentStep>
          </AgentProgress>
        )}
      </AIGenerateCard>

      <Card>
        <CardHeader>
          <CardTitle>Conteúdo</CardTitle>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <Label>Título *</Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Digite o título do post"
            />
          </FormGroup>

          <FormGroup>
            <Label>Slug (URL)</Label>
            <Input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              placeholder="url-do-post"
            />
            <HelpText>URL amigável para o post. Ex: meu-primeiro-post</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Resumo</Label>
            <Textarea
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Um breve resumo do post (exibido nas listagens)"
              $minHeight="80px"
            />
            <AIButton
              onClick={handleGenerateExcerpt}
              disabled={!formData.content || generatingExcerpt}
              $loading={generatingExcerpt}
            >
              {generatingExcerpt ? <FaSpinner /> : <FaMagic />}
              {generatingExcerpt ? 'Gerando...' : 'Gerar com IA'}
            </AIButton>
          </FormGroup>

          <FormGroup>
            <Label>Conteúdo</Label>
            <Textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Escreva o conteúdo do seu post aqui ou use a IA para gerar..."
              $minHeight="300px"
            />
            <HelpText>Você pode usar Markdown para formatar o texto.</HelpText>
          </FormGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Imagem de Capa</CardTitle>
        </CardHeader>
        <CardContent>
          <ImageUploadArea $hasImage={!!formData.cover_image} $image={formData.cover_image}>
            {formData.cover_image && (
              <RemoveImageButton onClick={handleRemoveImage}>
                <FaTimes />
              </RemoveImageButton>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
            />
            {!formData.cover_image && (
              <>
                <UploadIcon>
                  <FaImage />
                </UploadIcon>
                <UploadText>
                  {uploadingImage ? 'Enviando...' : 'Clique ou arraste uma imagem'}
                </UploadText>
              </>
            )}
            {formData.cover_image && (
              <ImageOverlay>
                <UploadIcon $hasImage>
                  <FaImage />
                </UploadIcon>
                <UploadText $hasImage>Clique para trocar a imagem</UploadText>
              </ImageOverlay>
            )}
          </ImageUploadArea>
          <HelpText>Recomendado: 1200x630 pixels para melhor exibição em redes sociais</HelpText>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Categorização</CardTitle>
        </CardHeader>
        <CardContent>
          <TwoColumns>
            <FormGroup>
              <Label>Categoria</Label>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Selecione uma categoria</option>
                <option value="tecnologia">Tecnologia</option>
                <option value="industria">Indústria 4.0</option>
                <option value="ia">Inteligência Artificial</option>
                <option value="inovacao">Inovação</option>
                <option value="cases">Cases de Sucesso</option>
                <option value="tutoriais">Tutoriais</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Data de Publicação</Label>
              <Input
                type="date"
                name="published_at"
                value={formData.published_at}
                onChange={handleChange}
              />
              <HelpText>Defina uma data retroativa para posts antigos</HelpText>
            </FormGroup>
          </TwoColumns>

          <FormGroup style={{ marginTop: '16px' }}>
            <Label>Tags</Label>
            <Input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="ia, robotica, automacao"
            />
            <AIButton
              onClick={handleGenerateTags}
              disabled={!formData.content || generatingTags}
              $loading={generatingTags}
            >
              {generatingTags ? <FaSpinner /> : <FaTags />}
              {generatingTags ? 'Gerando...' : 'Sugerir Tags'}
            </AIButton>
            <HelpText>Separe as tags por vírgula</HelpText>
          </FormGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SEO</CardTitle>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <Label>Meta Título</Label>
            <Input
              type="text"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleChange}
              placeholder="Título para buscadores (deixe vazio para usar o título do post)"
            />
          </FormGroup>

          <FormGroup>
            <Label>Meta Descrição</Label>
            <Textarea
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
              placeholder="Descrição para buscadores (deixe vazio para usar o resumo)"
              $minHeight="80px"
            />
            <AIButton
              onClick={handleGenerateMeta}
              disabled={!formData.content || !formData.title || generatingMeta}
              $loading={generatingMeta}
            >
              {generatingMeta ? <FaSpinner /> : <FaLightbulb />}
              {generatingMeta ? 'Gerando...' : 'Otimizar SEO'}
            </AIButton>
            <HelpText>Máximo recomendado: 160 caracteres</HelpText>
          </FormGroup>
        </CardContent>
      </Card>

      {/* Card do LinkedIn */}
      <LinkedInCard>
        <LinkedInTitle>
          <FaLinkedin />
          Compartilhar no LinkedIn
        </LinkedInTitle>
        <LinkedInSubtitle>
          Gere um post otimizado para o LinkedIn e publique diretamente ou copie para postar manualmente.
        </LinkedInSubtitle>

        {/* Status de conexão */}
        <LinkedInStatus className={linkedInConnected ? 'connected' : ''}>
          {linkedInConnected ? (
            <>
              <FaCheck /> Conta LinkedIn conectada
            </>
          ) : (
            <>
              Conta não conectada
            </>
          )}
        </LinkedInStatus>

        {/* Preview do post */}
        {linkedInPost && (
          <LinkedInPreview>
            {linkedInPost}
          </LinkedInPreview>
        )}

        {/* Upload de imagem para LinkedIn */}
        {linkedInPost && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaImage />
              Imagem do Post (opcional)
            </div>

            {!linkedInImagePreview ? (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* Botão para usar imagem de capa */}
                {formData.cover_image && (
                  <LinkedInButton
                    $variant="secondary"
                    onClick={handleUseLinkedInCoverImage}
                    type="button"
                  >
                    <FaImage />
                    Usar Capa do Blog
                  </LinkedInButton>
                )}

                {/* Upload de imagem customizada */}
                <label style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 200ms'
                }}>
                  <FaUpload />
                  Upload Imagem
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLinkedInImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            ) : (
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '300px'
              }}>
                <img
                  src={linkedInImagePreview}
                  alt="Preview LinkedIn"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    border: '2px solid rgba(255, 255, 255, 0.3)'
                  }}
                />
                <button
                  onClick={handleRemoveLinkedInImage}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.7)'
                }}>
                  {useLinkedInCoverImage ? 'Usando imagem de capa do blog' : linkedInImage?.name}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ações */}
        <LinkedInActions>
          <LinkedInButton
            onClick={handleGenerateLinkedInPost}
            disabled={!formData.content || !formData.title || generatingLinkedIn}
            $loading={generatingLinkedIn}
          >
            {generatingLinkedIn ? <FaSpinner /> : <FaMagic />}
            {generatingLinkedIn ? 'Gerando...' : 'Gerar Post'}
          </LinkedInButton>

          {linkedInPost && (
            <>
              <LinkedInButton
                $variant="secondary"
                onClick={handleCopyLinkedInPost}
              >
                <FaCopy />
                Copiar
              </LinkedInButton>

              {!linkedInConnected ? (
                <LinkedInButton
                  onClick={handleConnectLinkedIn}
                >
                  <FaLinkedin />
                  Conectar LinkedIn
                </LinkedInButton>
              ) : (
                <LinkedInButton
                  onClick={handlePublishLinkedIn}
                  disabled={publishingLinkedIn}
                  $loading={publishingLinkedIn}
                >
                  {publishingLinkedIn ? <FaSpinner /> : <FaLinkedin />}
                  {publishingLinkedIn ? 'Publicando...' : linkedInImage ? 'Publicar com Imagem' : 'Publicar'}
                </LinkedInButton>
              )}
            </>
          )}
        </LinkedInActions>
      </LinkedInCard>

      {showSuccess && (
        <SuccessMessage>
          Post {isEditing ? 'atualizado' : 'criado'} com sucesso!
        </SuccessMessage>
      )}
    </Container>
  );
};

export default BlogEditor;
